import { useEffect, useState } from "react";
import { Redirect, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useCreateOrg, useMe } from "@/lib/queries";
import { setActiveOrgSlug } from "@/lib/api";
import { ApiError } from "@/lib/api";

function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 24);
}

export default function Onboarding() {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [, setLocation] = useLocation();
  const create = useCreateOrg();
  const { data: me } = useMe();

  const effectiveSlug = slugTouched ? slug : suggestSlug(name);

  // Safety net: this page is the first-org-onboarding screen. If the user
  // already belongs to one or more orgs, send them home. Prevents the
  // accidental "create another org via URL" path until we ship a proper
  // create-additional-org UX.
  useEffect(() => {
    if (me && me.orgs.length > 0 && !create.isPending && !create.isSuccess) {
      setLocation("/dashboard");
    }
  }, [me, setLocation, create.isPending, create.isSuccess]);
  if (me && me.orgs.length > 0 && !create.isPending && !create.isSuccess) {
    return <Redirect to="/dashboard" />;
  }

  return (
    <div className="px-4 py-10 sm:py-16 max-w-xl mx-auto">
      <h1 className="text-3xl sm:text-4xl font-display uppercase tracking-tight">
        Start your league
      </h1>
      <p className="text-muted-foreground mt-2">
        An org is your backyard crew, your sports-bar circuit, or your tournament series.
      </p>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Create org</CardTitle>
          <CardDescription>You become the owner.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Hank's BBQ League"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">URL slug</label>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">cornholio.app /</span>
              <Input
                value={effectiveSlug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
                placeholder="hanks-bbq-league"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Lowercase letters, digits, hyphens. 2–32 chars.
            </p>
          </div>
          {create.isError && (
            <p className="text-sm text-destructive">
              {(create.error as ApiError).message ?? "Could not create org"}
            </p>
          )}
          <Button
            size="lg"
            className="w-full"
            disabled={!name.trim() || !effectiveSlug || create.isPending}
            onClick={async () => {
              try {
                const org = await create.mutateAsync({
                  name: name.trim(),
                  slug: effectiveSlug,
                });
                localStorage.setItem("activeOrgSlug", org.slug);
                setActiveOrgSlug(org.slug);
                setLocation("/dashboard");
              } catch {
                // surfaced via create.isError
              }
            }}
          >
            {create.isPending ? "Creating…" : "Create org"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
